/**
 * Maps items through an async function, at most `limit` at a time, keeping the
 * input order in the result.
 *
 * `onProgress` is never called once an item has failed. The merge wizard replaces
 * its whole progress state on each call: a slow conversion finishing after a fast
 * failure used to overwrite the error with « 3/4 » and leave the dialog spinning
 * forever (seen 2026-10-06, an e-mail conversion failing while a Word one ran).
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  map: (item: T) => Promise<R>,
  onProgress: (completed: number) => void,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  let completed = 0;
  let failed = false;

  const worker = async (): Promise<void> => {
    while (!failed && next < items.length) {
      const index = next++;
      try {
        // CAST: index < items.length is checked by the loop condition.
        results[index] = await map(items[index] as T);
      } catch (error) {
        failed = true;
        throw error;
      }
      if (!failed) onProgress(++completed);
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}
